import {
  RxListCompositeComponent,
  RxReactiveFormCompositeComponent,
  RxTextCompositeComponent,
  RxVideoCompositeComponent,
} from '@nrwl/rx-forms';

import { CustomBodyPartListModel } from '@nrwl/rx-models';
import { DialogsConfigService } from '../dialogs-config.service';
import { FormsValidatorsService } from '@nrwl/rx-forms-dialog-config';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RxTextareaComponent } from 'libs/rx-ui/src/lib/rx-textarea';
import { Validators } from '@angular/forms';
import { rxRequiredValidator } from '@nrwl/rx-validators';

@Injectable({
  providedIn: 'root',
})
export class AnatomyDialogConfigService extends DialogsConfigService {
  constructor(public formsValidatorsService: FormsValidatorsService) {
    super();
  }

  public createCaptureSequence(accountList: CustomBodyPartListModel[]) {
    const names = accountList.map((item) => item.name);
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'New Capture Sequence',
        tip: ` Capture Sequences are a series of areas in sequential order. You can name them based on body area, for example «Face»  or by treatment name for example «Body Counturing».

Portrait - Hold device vertically to capture sequence properly.
Landscape - Hold device horizontally to capture sequence properly.`,
        tipClose: true,
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        fields: [
          {
            placeholder: 'Sequence Name',
            labelText: null,
            hasInlineInfo: true,
            maxLength: 50,
            async: true,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
            asyncValidators: [this.formsValidatorsService.captureNameUniqValidator(names)],
          },
          {
            placeholder: 'Sequence Explanation',
            labelText: null,
            hasInlineInfo: true,
            maxLength: 40,
            async: true,
            validators: [Validators.maxLength(40)],
          },
        ],
      },
    };
  }

  public watchCaptureManagementTutorial() {
    return {
      component: RxVideoCompositeComponent,
      payload: {
        // TO DO: add url
        url: 'https://www.youtube.com/embed/nDikpRzZBjA',
        title: 'Help Center',
        buttons: this.DismissButton(),
      },
    };
  }

  public watchCaptureTutorial() {
    return {
      component: RxVideoCompositeComponent,
      payload: {
        // TO DO: add url
        url: 'https://www.youtube.com/embed/jrAe8cbCc1M',
        title: 'Help Center',
        buttons: this.DismissButton(),
      },
    };
  }

  public collapsedSequence() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: `Area(s) of anatomy selected in this sequence will be deselected by this action`,
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public deselectAllConfirmation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Deselect Area(s) of Anatomy',
        tip: 'All selected areas of anatomy will be deselected by this action.',
        buttons: this.SubmitDismissButtons('Yes', 'No'),
      },
    };
  }

  public alertDisableSelect() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip: 'You cannot select templates from both libraries',
        buttons: this.DismissButton(),
      },
    };
  }

  public confirmRemoveSequence() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Delete Area(s) of Anatomy',
        tip: 'All media associated with deleted area(s) of anatomy will remain active',
        buttons: this.SubmitDismissButtons('Proceed', 'Cancel'),
      },
    };
  }

  public addToAccountSequence(items$: Observable<CustomBodyPartListModel[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Add To',
        items$,
        placeholder: 'Search Capture Sequence...',
        buttons: this.SubmitDismissButtons('Add'),
      },
    };
  }

  public confirmRemovingBodyPart() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Delete Area(s) of Anatomy',
        tip: 'All media associated with deleted area(s) of anatomy will remain active',
        buttons: this.SubmitDismissButtons('Delete'),
      },
    };
  }

  public cloneBodyParts(items$: Observable<CustomBodyPartListModel[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Clone To',
        items$,
        placeholder: 'Search Capture Sequence...',
        buttons: this.SubmitDismissButtons('Clone'),
      },
    };
  }

  public moveToBodyParts(items$: Observable<CustomBodyPartListModel[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Move To',
        items$,
        placeholder: 'Search Capture Sequence...',
        buttons: this.SubmitDismissButtons('Move'),
      },
    };
  }

  public renameBodyPart(value: string = '') {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Rename Area of Anatomy',
        tip: 'All photos are catalogued by area of anatomy, providers can filter photos by these titles.',
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        fields: [
          {
            placeholder: 'Title',
            labelText: null,
            value,
            hasInlineInfo: true,
            maxLength: 50,
            async: true,
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(50)],
          },
        ],
      },
    };
  }

  public renameBodyPartList(
    accountList: CustomBodyPartListModel[],
    value: string = '',
    validateOnInit = true,
    checkOutside = false,
  ) {
    const names = accountList
      .map((item) => item.name)
      .filter((item) => (!checkOutside ? item !== value : true));
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Rename Capture Sequence',
        tip: 'All sequences must have title',
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        fields: [
          {
            placeholder: 'Sequence Name',
            labelText: null,
            hasInlineInfo: true,
            value,
            maxLength: 50,
            async: true,
            validators: [rxRequiredValidator, Validators.maxLength(50)],
            asyncValidators: [this.formsValidatorsService.captureNameUniqValidator(names)],
          },
        ],
        validateOnInit,
      },
    };
  }

  public modifyOnScreenTip(value: string = '') {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Modify On-screen Tip',
        tip: 'On-screen tips help providers capture standardized and consistent photos',
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        fields: [
          {
            component: RxTextareaComponent,
            placeholder: 'Tip',
            hasInlineInfo: true,
            value,
            maxLength: 120,
            validators: [Validators.maxLength(120)],
          },
        ],
      },
    };
  }

  public modifySequenceExplanation(value: string = '') {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Modify Sequence Explanation',
        tip: 'All sequences must have an explanation',
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        fields: [
          {
            placeholder: 'Sequence Explanation ',
            hasInlineInfo: true,
            value,
            maxLength: 40,
            async: true,
            validators: [Validators.maxLength(40)],
          },
        ],
      },
    };
  }

  public renameExistingMedia() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Rename existing photos and videos?',
        tip: 'You are about to rename the area of anatomy for all new photos and videos captured in the future. Do you want to rename all existing items to the new area of anatomy? Please note that media previously pushed to your 3rd party integration partner will not reflect this change.',
        buttons: this.SubmitCustomButtons(
          'Yes, update all existing items',
          'No, only use the new name moving forward',
        ),
      },
    };
  }

  public confirmDelete() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'On-screen Positioning Guide',
        tip: `Are you sure you want to delete this on-screen positioning guide?<br>Please note that it might be linked to body part(s).`,
        tipClose: true,
        buttons: this.SubmitDismissButtons('Delete', 'Cancel'),
      },
    };
  }

  public discardBodyPartTemplateChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Warning',
        tip: `You have unsaved changes. Press "Discard" to revert unsaved changes or "Save and Proceed" to save your changes.`,
        buttons: this.SubmitDismissButtons('Save and Proceed', 'Discard'),
      },
    };
  }

  public deleteBodyPartTemplate() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Warning',
        tip: `Are you sure you want to delete this on-screen positioning guide? Please note that it might be linked to body part(s).`,
        buttons: this.SubmitDismissButtons('Delete', 'Cancel'),
      },
    };
  }

  public reflectedChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Capture Management',
        tip: `Changes will be reflected in the RxPhoto mobile app 10 minutes after the last update. To view changes immediately, please refresh the capture list in the mobile app:<br />
                <img src="assets/images/capture_instruction.png" style="margin: 0 auto; width: 60%">`,
        buttons: this.SubmitButton('I understand'),
      },
    };
  }
}
